"use client";

import { useEffect, useId, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { MobileBottomSheet } from "@/components/shared/MobileBottomSheet";
import { useLanguage } from "@/contexts/LanguageContext";

export interface ConsentModalProps {
  open?: boolean;
  mobile: string;
  locale?: string;
  onModelClose: () => void;
  pageName?: string;
  pageCategory?: string;
  eventCategory?: string;
}

type NotificationKeys =
  | "email"
  | "sms"
  | "whatsapp"
  | "push_notifications"
  | "location_access";

type NotificationsState = Record<NotificationKeys, boolean>;

type ConsentApiResponse = {
  success: boolean;
  data?: {
    preferences?: Partial<NotificationsState>;
    message?: string;
  };
  message?: string;
};

function DesktopConsentDialog({
  open,
  title,
  closeLabel,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  closeLabel: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-0 z-[120] hidden items-center justify-center bg-black/45 p-4 backdrop-blur-[1px] md:flex">
      <button
        type="button"
        className="absolute inset-0"
        aria-label={closeLabel}
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative flex max-h-[min(92vh,860px)] w-full sm:w-[500px] flex-col overflow-hidden rounded-[28px] border border-white/20 bg-white shadow-[0_24px_80px_rgba(0,0,0,0.28)]"
      >
        <div className="flex items-start justify-between gap-3 px-6 pb-2 pt-6">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[#1F2937] hover:bg-[#F4F4F4]"
              aria-label={closeLabel}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="m15 18-6-6 6-6" />
              </svg>
            </button>
            <h2 id={titleId} className="md:text-[24px] text-[20px] font-semibold leading-[1.03] text-[#212121]">
              {title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F2F2F2] text-[#6B7280] hover:bg-[#E7E7E7]"
            aria-label={closeLabel}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-6 custom-scrollbar">{children}</div>
      </div>
    </div>,
    document.body
  );
}

export default function ConsentModal({
  open = true,
  mobile,
  onModelClose,
  pageName,
  pageCategory,
  eventCategory,
}: ConsentModalProps) {
  const { t } = useLanguage();
  const [loader, setLoader] = useState(false);
  const [notifications, setNotifications] = useState<NotificationsState>({
    email: false,
    sms: false,
    whatsapp: false,
    push_notifications: false,
    location_access: false,
  });

  void pageName;
  void pageCategory;
  void eventCategory;

  const title = t("consent.title");
  const closeLabel = t("common.close");

  /** One lock for both mobile + desktop layers (same pattern as LoginDialog). */
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !mobile?.trim()) return;

    let cancelled = false;
    const run = async () => {
      setLoader(true);
      try {
        const res = await fetch(
          `/api/customer/consent?mobile=${encodeURIComponent(mobile.trim())}`,
          { method: "GET", cache: "no-store" }
        );
        const consent = (await res.json().catch(() => null)) as ConsentApiResponse | null;
        if (cancelled) return;
        if (consent?.success && consent?.data?.preferences) {
          setNotifications({
            email: consent.data.preferences.email ?? false,
            sms: consent.data.preferences.sms ?? false,
            whatsapp: consent.data.preferences.whatsapp ?? false,
            push_notifications: consent.data.preferences.push_notifications ?? false,
            location_access: consent.data.preferences.location_access ?? false,
          });
        }
      } finally {
        if (!cancelled) setLoader(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [open, mobile]);

  const updateConsent = async (updatedState: NotificationsState) => {
    if (!mobile?.trim()) return;
    setLoader(true);
    try {
      await fetch("/api/customer/consent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone: mobile.trim(),
          preferences: updatedState,
        }),
      });
    } finally {
      setLoader(false);
    }
  };

  const toggleConsent = (key: NotificationKeys) => {
    setNotifications((prev) => {
      const updatedState = { ...prev, [key]: !prev[key] };
      void updateConsent(updatedState);
      return updatedState;
    });
  };

  const communicationItems: ReadonlyArray<{
    key: Exclude<NotificationKeys, "location_access">;
    title: string;
    desc: string;
  }> = [
    { key: "email", title: "consent.email", desc: "consent.email_desc" },
    { key: "sms", title: "consent.sms", desc: "consent.sms_desc" },
    { key: "whatsapp", title: "consent.whatsapp", desc: "consent.whatsapp_desc" },
    {
      key: "push_notifications",
      title: "consent.push_notifications",
      desc: "consent.push_notifications_desc",
    },
  ];

  const content = (
    <div className="relative">
      {loader ? (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/70 backdrop-blur-[1px]">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#DD0428] border-t-transparent" />
        </div>
      ) : null}

      <section className="pb-4">
        <p className="mb-2 text-[18px] font-semibold leading-[20px] text-[#212121] md:text-[20px] md:leading-[26px]">
          {t("consent.subtitle")}
        </p>
        <p className="text-[13px] font-normal leading-[1.6] text-[#212121] md:text-[15px]">
          {t("consent.desc")}
        </p>

        <div className="my-8 border-t border-[#D9D9D9]" />

        <h2 className="mb-2 text-[20px] font-semibold leading-[26px] text-[#212121] md:text-[24px] md:leading-[28px]">
          {t("consent.communication_preferences")}
        </h2>
        <p className="mb-7 text-[13px] font-normal leading-[1.6] text-[#212121] md:text-[15px]">
          {t("consent.preference_desc")}
        </p>

        <div className="flex flex-col gap-4">
          {communicationItems.map((item) => (
            <div key={item.key} className="flex items-start justify-between gap-3 py-1">
              <div className="flex-1">
                <p className="text-[15px] font-semibold leading-[1.12] text-[#212121] md:text-[18px]">
                  {t(item.title)}
                </p>
                <p className="mt-2 text-[13px] font-normal leading-[1.45] text-[#212121] md:text-[15px]">
                  {t(item.desc)}
                </p>
              </div>
              <label className="relative mt-3 inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  className="peer sr-only"
                  checked={notifications[item.key]}
                  onChange={() => toggleConsent(item.key)}
                />
                <div className="relative h-[36px] w-[68px] rounded-full bg-[#D9D9D9] transition-all duration-300 peer-checked:bg-[#000000]">
                  <div
                    className={`absolute left-1 top-1 h-[28px] w-[28px] rounded-full bg-white transition-transform duration-300 ${
                      notifications[item.key] ? "translate-x-8" : ""
                    }`}
                  />
                </div>
              </label>
            </div>
          ))}
        </div>
      </section>
    </div>
  );

  return (
    <>
      <MobileBottomSheet
        open={open}
        title={title}
        closeLabel={closeLabel}
        onClose={onModelClose}
        lockBodyScroll={false}
      >
        <div className="px-2">{content}</div>
      </MobileBottomSheet>
      <DesktopConsentDialog
        open={open}
        title={title}
        closeLabel={closeLabel}
        onClose={onModelClose}
      >
        {content}
      </DesktopConsentDialog>
    </>
  );
}
